import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { calculateTotal, calculateGrandTotal, validateOrderQuantities, generateOrderSummary, handleApiError, getETopUpMultiplier } from '../utils/helpers';
import { createOrder, getClients, getAccountInfo, getWholesalerById, getDenominations } from '../services/api';
import { validateTokenState } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/common/Modal';

const style = `
.scratch-input-no-spinner input[type=number]::-webkit-inner-spin-button, 
.scratch-input-no-spinner input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.scratch-input-no-spinner input[type=number] {
  -moz-appearance: textfield;
}
`;

// Helper function for comma formatting
const formatNumber = (num) => Number(num).toLocaleString('en-US');
// Sanitize note input to mitigate injection/XSS and limit length
const sanitizeNote = (text) => {
    if (typeof text !== 'string') return '';
    const withoutControl = text.replace(/[\u0000-\u001F\u007F]/g, '');
    const withoutTags = withoutControl.replace(/[<>`]/g, '');
    return withoutTags.slice(0, 250);
};

const OrderRequest = () => {
    const mainContentRef = useRef(null);
    // Robust scrollToTop helper
    const scrollToTop = () => {
        // Try main content ref
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // Try window
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Try all scrollable elements
        const scrollables = document.querySelectorAll('[style*="overflow"], .scrollable, .main-content, main, body, html');
        scrollables.forEach(el => {
            try {
                el.scrollTo({ top: 0, behavior: 'smooth' });
            } catch { }
        });
        // Fallback: create a temp element and scroll into view
        const temp = document.createElement('div');
        temp.style.position = 'absolute';
        temp.style.top = '0px';
        document.body.prepend(temp);
        temp.scrollIntoView({ behavior: 'smooth' });
        temp.remove();
    };

    const navigate = useNavigate();

    const scratchWarehouses = [
        "Erbil Warehouse",
        "Dohuk Warehouse",
        "Suly Warehouse",
        "Kirkuk Warehouse",
        "Mosul Warehouse",
        "Baghdad Warehouse"
    ];

    const [denomsLoading, setDenomsLoading] = useState(true);
    const [denomsError, setDenomsError] = useState('');
    const [scratchDenominations, setScratchDenominations] = useState([]);
    const [denominations, setDenominations] = useState({
        ePin: [],
        eTopUp: [{ label: 'Amount', value: getETopUpMultiplier() }],
    });

    const [quantities, setQuantities] = useState({
        scratch: { A: 0, B: 0 },
        eTopUp: { Amount: 0 },
    });
    const [activeTab, setActiveTab] = useState('ePin');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [selectedClients, setSelectedClients] = useState([]);
    const [clientOrders, setClientOrders] = useState({});
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [clientsError, setClientsError] = useState(null);
    const { accessToken, logout } = useAuth();
    const [etopUpNote, setEtopUpNote] = useState('');

    // Add state for user role
    const [userRole, setUserRole] = useState(null);
    const [roleLoading, setRoleLoading] = useState(true);
    const [roleError, setRoleError] = useState(null);

    // Helper for KTE Employees roles
    const isKTEEmployee = userRole === 'KTE-Employees' || userRole === 'KTE-Employees-test';

    // Fetch user role on mount
    useEffect(() => {
        const fetchRole = async () => {
            setRoleLoading(true);
            setRoleError(null);
            const tokenState = validateTokenState();
            if (tokenState.needsLogin && !tokenState.accessTokenValid) {
                logout();
                navigate('/login', { replace: true });
                return;
            }
            try {
                const result = await getAccountInfo();
                // Try to get role from result (adjust field name if needed)
                setUserRole(result.role || result.accountType || null);
            } catch (err) {
                const latestTokenState = validateTokenState();
                if (latestTokenState.needsLogin && !latestTokenState.accessTokenValid) {
                    logout();
                    navigate('/login', { replace: true });
                    return;
                }
                setRoleError('Failed to fetch user role');
            } finally {
                setRoleLoading(false);
            }
        };
        fetchRole();
    }, [accessToken, logout, navigate]);

    // Set default tab to 'scratch' for KTE-Employees roles
    useEffect(() => {
        if (isKTEEmployee) {
            setActiveTab('scratch');
        }
    }, [userRole]);

    const [scratchSelections, setScratchSelections] = useState({});
    useEffect(() => {
        const initial = {};
        scratchDenominations.forEach(denom => {
            initial[denom.label] = {};
            scratchWarehouses.forEach(warehouse => {
                initial[denom.label][warehouse] = 0;
            });
        });
        setScratchSelections(initial);
    }, [scratchDenominations]);

    const [clientContactInfo, setClientContactInfo] = useState({});
    const [clientContactErrors, setClientContactErrors] = useState({});
    const [wholesalerInfo, setWholesalerInfo] = useState({ id: '', name: '' });
    const [wholesalerLoading, setWholesalerLoading] = useState(false);
    const [wholesalerError, setWholesalerError] = useState('');
    const wholesalerDebounceRef = useRef();
    const lastRequestedIdRef = useRef('');
    const wholesalerIdInputRef = useRef();

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                setDenomsLoading(true);
                setDenomsError('');
                const data = await getDenominations();
                const scratch = [];
                const epin = [];
                let topUpVal = null;
                if (Array.isArray(data)) {
                    data.forEach((d) => {
                        const type = String(d.orderType || '').toUpperCase();
                        const denom = Number(d.denomination);
                        const price = Number(d.price);
                        if (!Number.isFinite(price)) return;
                        if (type === 'SCRATCH' && Number.isFinite(denom)) {
                            scratch.push({ label: denom.toLocaleString('en-US'), value: price });
                        } else if (type === 'EPIN' && Number.isFinite(denom)) {
                            epin.push({ label: denom.toLocaleString('en-US'), value: price });
                        } else if (type === 'E_TOPUP') {
                            topUpVal = price;
                        }
                    });
                }
                epin.sort((a, b) => Number(a.label.replace(/,/g, '')) - Number(b.label.replace(/,/g, '')));
                scratch.sort((a, b) => Number(a.label.replace(/,/g, '')) - Number(b.label.replace(/,/g, '')));
                if (mounted) {
                    setScratchDenominations(scratch.length ? scratch : [
                        { label: "3,000", value: 3300 },
                        { label: "5,000", value: 5150 },
                        { label: "10,000", value: 10250 },
                        { label: "15,000", value: 15300 },
                        { label: "30,000", value: 30000 },
                        { label: "60,000", value: 60000 }
                    ]);
                    setDenominations({
                        ePin: epin.length ? epin : [
                            { label: '1,000', value: 1300 },
                            { label: '2,000', value: 2375 },
                            { label: '3,000', value: 3300 },
                            { label: '4,000', value: 4300 },
                            { label: '5,000', value: 5125 },
                            { label: '6,000', value: 6125 },
                            { label: '10,000', value: 10200 },
                            { label: '12,000', value: 12240 },
                            { label: '15,000', value: 15250 },
                            { label: '18,000', value: 18250 },
                            { label: '30,000', value: 30000 },
                            { label: '60,000', value: 60000 }
                        ],
                        eTopUp: [{ label: 'Amount', value: Number(topUpVal || getETopUpMultiplier()) }],
                    });
                }
            } catch (e) {
                if (mounted) {
                    setDenomsError('Failed to load denominations');
                    setScratchDenominations([
                        { label: "3,000", value: 3300 },
                        { label: "5,000", value: 5150 },
                        { label: "10,000", value: 10250 },
                        { label: "15,000", value: 15300 },
                        { label: "30,000", value: 30000 },
                        { label: "60,000", value: 60000 }
                    ]);
                    setDenominations({
                        ePin: [
                            { label: '1,000', value: 1300 },
                            { label: '2,000', value: 2375 },
                            { label: '3,000', value: 3300 },
                            { label: '4,000', value: 4300 },
                            { label: '5,000', value: 5125 },
                            { label: '6,000', value: 6125 },
                            { label: '10,000', value: 10200 },
                            { label: '12,000', value: 12240 },
                            { label: '15,000', value: 15250 },
                            { label: '18,000', value: 18250 },
                            { label: '30,000', value: 30000 },
                            { label: '60,000', value: 60000 }
                        ],
                        eTopUp: [{ label: 'Amount', value: getETopUpMultiplier() }],
                    });
                }
            } finally {
                if (mounted) setDenomsLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [accessToken]);

    const warehouseCodeMap = {
        'Erbil Warehouse': 'EBL_WH',
        'Dohuk Warehouse': 'DUH_WH',
        'Suly Warehouse': 'SUL_WH',
        'Kirkuk Warehouse': 'KRK_WH',
        'Mosul Warehouse': 'MSL_WH',
        'Baghdad Warehouse': 'BGD_WH',
    };

    const handleQuantityChange = (type, label, value) => {
        const numValue = parseInt(value) || 0;
        setQuantities(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [label]: numValue
            }
        }));

        // Clear errors when user makes changes
        if (errors[type]) {
            setErrors(prev => ({
                ...prev,
                [type]: ''
            }));
        }
    };

    const handleClientQuantityChange = (clientId, denomination, value) => {
        const numValue = parseInt(value) || 0;
        setClientOrders(prev => ({
            ...prev,
            [clientId]: {
                ...prev[clientId],
                [denomination]: numValue
            }
        }));
    };

    const handleClientContactChange = (clientId, field, value) => {
        setClientContactInfo(prev => ({
            ...prev,
            [clientId]: {
                ...prev[clientId],
                [field]: value
            }
        }));
    };

    const calculateClientTotal = (clientId) => {
        if (!clientOrders[clientId]) return 0;
        return Math.round(Object.entries(clientOrders[clientId]).reduce((sum, [denom, qty]) => {
            const denomination = denominations.ePin.find(d => d.label === denom);
            return sum + (denomination.value * qty);
        }, 0));
    };

    const calculateEpinTotal = () => {
        return Math.round(selectedClients.reduce((total, clientId) => {
            return total + calculateClientTotal(clientId);
        }, 0));
    };

    const epinErrorRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if there's at least one quantity across all types (including scratch cards)
        const hasEpinQuantity = selectedClients.some(clientId => {
            const clientOrder = clientOrders[clientId];
            return clientOrder && Object.values(clientOrder).some(qty => qty > 0);
        });
        const hasETopUpQuantity = Object.values(quantities.eTopUp).some(qty => qty > 0);
        const hasScratchQuantity = Object.values(scratchSelections).some(warehouseSelections =>
            Object.values(warehouseSelections).some(qty => qty > 0)
        );

        // Specific validation: note present without any order amounts
        if (!hasEpinQuantity && !hasETopUpQuantity && !hasScratchQuantity && sanitizeNote(etopUpNote).trim().length > 0) {
            setErrors({ eTopUp: 'Please enter an E-TopUp amount to submit a note.' });
            setActiveTab('eTopUp');
            return;
        }

        if (!hasEpinQuantity && !hasETopUpQuantity && !hasScratchQuantity) {
            setErrors({ general: "Please select at least one quantity for any order type" });
            return;
        }

        // Validate client selection for E-PIN orders
        if (hasEpinQuantity && selectedClients.length === 0) {
            setErrors({ ePin: "Please select at least one client for E-PIN orders" });
            return;
        }

        // Validate phone and email for each selected client with E-PIN quantity
        let contactErrors = {};
        let missingContact = false;
        selectedClients.forEach(clientId => {
            const clientOrder = clientOrders[clientId];
            if (clientOrder && Object.values(clientOrder).some(qty => qty > 0)) {
                const contact = clientContactInfo[clientId] || {};
                const client = clients.find(c => c.id.toString() === clientId);
                const phone = (contact.phoneNumber !== undefined ? contact.phoneNumber : (client.phoneNumber || client.phone || ''))?.trim();
                const email = (contact.email !== undefined ? contact.email : (client.email || ''))?.trim();
                if (!phone) {
                    contactErrors[clientId] = contactErrors[clientId] || {};
                    contactErrors[clientId].phoneNumber = 'Phone number is required';
                    missingContact = true;
                }
                if (!email) {
                    contactErrors[clientId] = contactErrors[clientId] || {};
                    contactErrors[clientId].email = 'Email is required';
                    missingContact = true;
                }
            }
        });
        setClientContactErrors(contactErrors);
        if (missingContact) {
            setErrors({ ePin: 'Please provide phone number and email for all selected clients.' });
            setActiveTab('ePin'); // Redirect to E-PIN tab
            // Scroll to E-PIN error
            setTimeout(() => {
                if (epinErrorRef.current) {
                    epinErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }

        if (isKTEEmployee && (!wholesalerInfo.id.trim() || !wholesalerInfo.name.trim())) {
            setWholesalerError('Please enter a valid wholesaler ID');
            if (wholesalerIdInputRef.current) {
                wholesalerIdInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Validate calculations before submission
        const calculatedEpinTotal = calculateEpinTotal();
        const calculatedScratchTotal = calculateScratchTotal();
        const calculatedETopUpTotal = calculateTotal('eTopUp', quantities, denominations);
        const calculatedGrandTotal = calculatedEpinTotal + calculatedScratchTotal + calculatedETopUpTotal;

        // Log calculations for debugging
        console.log('Order Request Calculations:', {
            epinTotal: calculatedEpinTotal,
            scratchTotal: calculatedScratchTotal,
            eTopUpTotal: calculatedETopUpTotal,
            grandTotal: calculatedGrandTotal,
            scratchSelections,
            clientOrders,
            quantities
        });

        // Verify that calculated total matches displayed total
        if (Math.abs(calculatedGrandTotal - grandTotal) > 0.01) {
            console.error('Calculation mismatch detected:', {
                calculated: calculatedGrandTotal,
                displayed: grandTotal,
                difference: calculatedGrandTotal - grandTotal
            });
            setErrors({ general: "Calculation error detected. Please refresh the page and try again." });
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);
        setErrors({});

        try {
            // Build API request body
            // 1. etopUpAmount
            const etopUpAmount = Number(quantities.eTopUp.Amount) || 0;
            const etopUpNoteSanitized = sanitizeNote(etopUpNote);

            // 2. scratchCards
            const scratchCards = [];
            Object.entries(scratchSelections).forEach(([denomLabel, warehouseObj]) => {
                const denomObj = scratchDenominations.find(d => d.label === denomLabel);
                if (!denomObj) return;
                Object.entries(warehouseObj).forEach(([warehouse, qty]) => {
                    if (qty > 0) {
                        const card = {
                            warehouse: warehouseCodeMap[warehouse] || warehouse,
                            denomination: parseInt(denomLabel.replace(/,/g, '')), // face value
                            price: denomObj.value, // price
                            quantity: qty,
                        };
                        scratchCards.push(card);
                    }
                });
            });

            // 3. epins
            const epins = [];
            selectedClients.forEach(clientId => {
                const client = clients.find(c => c.id.toString() === clientId);
                if (!client) return;
                const orders = clientOrders[clientId] || {};
                const contact = clientContactInfo[clientId] || {};
                Object.entries(orders).forEach(([denomLabel, qty]) => {
                    if (qty > 0) {
                        const denomObj = denominations.ePin.find(d => d.label === denomLabel);
                        if (!denomObj) return;
                        epins.push({
                            clientId: client.id,
                            phoneNumber: contact.phoneNumber || client.phoneNumber || client.phone || '',
                            email: contact.email || client.email || '',
                            denomination: parseInt(denomLabel.replace(/,/g, '')), // face value
                            price: denomObj.value, // price
                            quantity: qty,
                        });
                    }
                });
            });

            // Build orderData object
            let orderData = {
                scratchCards,
                epins,
            };
            if (etopUpAmount > 0) {
                // Backend expects eTopUp structure
                orderData.eTopUp = {
                    amount: String(etopUpAmount),
                    note: etopUpNoteSanitized || null,
                };
                // Backward-compatible legacy fields
                orderData.etopUpAmount = etopUpAmount;
                if (etopUpNoteSanitized) orderData.note = etopUpNoteSanitized;
            }
            if (isKTEEmployee) {
                if (wholesalerInfo.id) orderData.wholesalerId = wholesalerInfo.id;
                if (wholesalerInfo.name) orderData.merchantName = wholesalerInfo.name;
            }

            // Submit order using real API
            const result = await createOrder(orderData);

            setSubmitStatus({
                type: 'success',
                message: result.message || `Order submitted successfully! Order ID: ${result.orderId || result.id}`,
                orderId: result.orderId || result.id
            });
            scrollToTop();
            setShowSuccessModal(true);
            // Reset all input fields after successful submission (but do NOT clear submitStatus here)
            setQuantities({
                scratch: { A: 0, B: 0 },
                eTopUp: { Amount: 0 },
            });
            setEtopUpNote('');
            setScratchSelections(() => {
                const initial = {};
                scratchDenominations.forEach(denom => {
                    initial[denom.label] = {};
                    scratchWarehouses.forEach(warehouse => {
                        initial[denom.label][warehouse] = 0;
                    });
                });
                return initial;
            });
            setSelectedClients([]);
            setClientOrders({});
            setClientContactInfo({});
            setErrors({});
            if (isKTEEmployee) {
                setWholesalerInfo({ id: '', name: '' });
                setWholesalerError('');
            }

        } catch (error) {
            if (handleApiError(error, navigate)) return;
            setSubmitStatus({
                type: 'error',
                message: 'Failed to submit order. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setQuantities({
            scratch: { A: 0, B: 0 },
            eTopUp: { Amount: 0 },
        });
        setEtopUpNote('');
        setScratchSelections(() => {
            const initial = {};
            scratchDenominations.forEach(denom => {
                initial[denom.label] = {};
                scratchWarehouses.forEach(warehouse => {
                    initial[denom.label][warehouse] = 0;
                });
            });
            return initial;
        });
        setSelectedClients([]);
        setClientOrders({});
        setClientContactInfo({}); // Reset contact info
        setErrors({});
        setSubmitStatus(null);
        scrollToTop();
    };

    const renderOrderSection = (type, title) => (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

            {errors[type] && (
                <div ref={type === 'ePin' ? epinErrorRef : undefined} className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
                    {errors[type]}
                </div>
            )}

            {type === 'ePin' && (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select Clients
                        </label>
                        {clientsLoading ? (
                            <div className="text-gray-500 text-sm">Loading clients...</div>
                        ) : clientsError ? (
                            <div className="text-red-500 text-sm">{clientsError}</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {clients.map(client => {
                                    const isSelected = selectedClients.includes(client.id.toString());
                                    return (
                                        <div
                                            key={client.id}
                                            className={`p-3 border-2 rounded-lg cursor-pointer transition-colors duration-200 ${isSelected
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-300 bg-white hover:border-gray-400'
                                                }`}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedClients(prev => prev.filter(id => id !== client.id.toString()));
                                                    setClientOrders(prev => {
                                                        const newOrders = { ...prev };
                                                        delete newOrders[client.id];
                                                        return newOrders;
                                                    });
                                                    setClientContactInfo(prev => {
                                                        const newInfo = { ...prev };
                                                        delete newInfo[client.id];
                                                        return newInfo;
                                                    });
                                                } else {
                                                    setSelectedClients(prev => [...prev, client.id.toString()]);
                                                }
                                            }}
                                        >
                                            <div className="text-sm font-medium text-gray-900">{client.name}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {selectedClients.length > 0 && (
                        <div className="space-y-4">
                            {selectedClients.map(clientId => {
                                const client = clients.find(c => c.id.toString() === clientId);
                                if (!client) return null;
                                const contact = clientContactInfo[clientId] || {};
                                const contactError = clientContactErrors[clientId] || {};
                                return (
                                    <div key={clientId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                                            {client.name}
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-3 py-2 border ${contactError.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                    value={contact.phoneNumber !== undefined ? contact.phoneNumber : (client.phoneNumber || client.phone || '')}
                                                    onChange={e => handleClientContactChange(clientId, 'phoneNumber', e.target.value)}
                                                />
                                                {contactError.phoneNumber && (
                                                    <div className="text-xs text-red-500 mt-1">{contactError.phoneNumber}</div>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                                                <input
                                                    type="email"
                                                    className={`w-full px-3 py-2 border ${contactError.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                    value={contact.email !== undefined ? contact.email : (client.email || '')}
                                                    onChange={e => handleClientContactChange(clientId, 'email', e.target.value)}
                                                />
                                                {contactError.email && (
                                                    <div className="text-xs text-red-500 mt-1">{contactError.email}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {denominations.ePin.map((denom) => (
                                                <div key={denom.label} className="space-y-2">
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        {denom.label} (IQD {formatNumber(denom.value)})
                                                    </label>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9,]*"
                                                        value={
                                                            clientOrders[clientId]?.[denom.label] > 0
                                                                ? formatNumber(clientOrders[clientId][denom.label])
                                                                : clientOrders[clientId]?.[denom.label] === 0
                                                                    ? ''
                                                                    : clientOrders[clientId]?.[denom.label] || ''
                                                        }
                                                        onChange={(e) => {
                                                            const raw = e.target.value.replace(/,/g, "");
                                                            handleClientQuantityChange(clientId, denom.label, raw);
                                                        }}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <p className="text-sm text-gray-600">
                                                Client Total: <span className="font-semibold text-gray-900">IQD {formatNumber(calculateClientTotal(clientId))}</span>
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                            Total: <span className="font-semibold text-gray-900">IQD {formatNumber(calculateEpinTotal())}</span>
                        </p>
                    </div>
                </div>
            )}

            {type === 'eTopUp' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Enter Amount (IQD)
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9,]*"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                            value={
                                quantities[type].Amount > 0
                                    ? formatNumber(quantities[type].Amount)
                                    : ""
                            }
                            onChange={e => {
                                const raw = e.target.value.replace(/,/g, "");
                                const qty = parseInt(raw) || 0;
                                setQuantities(prev => ({
                                    ...prev,
                                    [type]: {
                                        ...prev[type],
                                        Amount: raw === "" ? "" : qty
                                    }
                                }));
                            }}
                            placeholder="0"
                        />
                        <p className="mt-2 text-sm text-gray-500">
                            Price will be calculated as: Amount × {Number(denominations.eTopUp[0]?.value || 0).toFixed(3)}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Note (optional, max 250 characters)
                        </label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                            value={etopUpNote}
                            maxLength={250}
                            onChange={(e) => setEtopUpNote(sanitizeNote(e.target.value))}
                            placeholder="Add an optional note about this E-TopUp"
                            rows={3}
                        />
                        <div className="mt-1 text-xs text-gray-500">{etopUpNote.length}/250</div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                            Total: <span className="font-semibold text-gray-900">IQD {formatNumber(calculateTotal(type, quantities, denominations))}</span>
                        </p>
                    </div>
                </div>
            )}

            {type !== 'ePin' && type !== 'eTopUp' && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {denominations[type].map((denom) => (
                            <div key={denom.label} className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    {denom.label} (IQD {formatNumber(denom.value)})
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={quantities[type][denom.label]}
                                    onChange={(e) => handleQuantityChange(type, denom.label, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                />
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                            Total: <span className="font-semibold text-gray-900">IQD {formatNumber(calculateTotal(type, quantities, denominations))}</span>
                        </p>
                    </div>
                </>
            )}
        </div>
    );

    const renderScratchSection = () => (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Scratch Card Orders</h3>
            <div className="overflow-x-auto scratch-input-no-spinner w-full border border-gray-200 rounded-lg shadow-sm">
                <table className="w-full min-w-[600px] text-sm divide-y divide-gray-200">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</th>
                            {scratchDenominations.map(denom => (
                                <th key={denom.label} className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {denom.label}<br />
                                    <span className="text-[10px] font-normal text-gray-400">IQD {formatNumber(denom.value)}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {scratchWarehouses.map(warehouse => (
                            <tr key={warehouse} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{warehouse}</td>
                                {scratchDenominations.map(denom => (
                                    <td key={denom.label} className="px-4 py-3 whitespace-nowrap">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9,]*"
                                            className="w-full max-w-[120px] mx-auto block px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center appearance-none placeholder-gray-400 bg-white"
                                            value={(() => {
                                                const v = scratchSelections?.[denom.label]?.[warehouse];
                                                if (typeof v === 'number' && v > 0) return formatNumber(v);
                                                return v === 0 || v === "" ? "" : (v || "");
                                            })()}
                                            onChange={e => {
                                                // Remove all commas before parsing
                                                const raw = e.target.value.replace(/,/g, "");
                                                const qty = parseInt(raw) || 0;
                                                setScratchSelections(prev => {
                                                    const ensuredRow = prev[denom.label] || scratchWarehouses.reduce((acc, w) => {
                                                        acc[w] = 0;
                                                        return acc;
                                                    }, {});
                                                    return {
                                                        ...prev,
                                                        [denom.label]: {
                                                            ...ensuredRow,
                                                            [warehouse]: raw === "" ? "" : qty
                                                        }
                                                    };
                                                });
                                            }}
                                            placeholder="0"
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Calculate scratch card total
    const calculateScratchTotal = () => {
        return Object.entries(scratchSelections).reduce((sum, [denomLabel, selection]) => {
            const denomination = scratchDenominations.find(d => d.label === denomLabel);
            const warehouseSum = Object.values(selection).reduce((warehouseSum, qty) => warehouseSum + (qty > 0 ? denomination.value * qty : 0), 0);
            return sum + warehouseSum;
        }, 0);
    };

    const ePinTotal = calculateEpinTotal();
    const scratchTotal = calculateScratchTotal();
    const eTopUpTotal = calculateTotal('eTopUp', quantities, denominations);
    const grandTotal = ePinTotal + scratchTotal + eTopUpTotal;

    // Refetch clients on accessToken change or window focus
    useEffect(() => {
        const fetchClients = async () => {
            setClientsLoading(true);
            setClientsError(null);
            try {
                const data = await getClients();
                setClients(data);
            } catch (error) {
                setClientsError('Failed to load clients.');
            } finally {
                setClientsLoading(false);
            }
        };
        fetchClients();
    }, [accessToken]);

    // Add effect to fetch wholesaler name when ID changes (for KTE-Employees roles)
    useEffect(() => {
        if (isKTEEmployee) {
            if (wholesalerDebounceRef.current) {
                clearTimeout(wholesalerDebounceRef.current);
            }
            const trimmedId = wholesalerInfo.id.trim();
            if (!trimmedId) {
                setWholesalerInfo(info => ({ ...info, name: '' }));
                setWholesalerError('');
                setWholesalerLoading(false);
                return;
            }
            setWholesalerLoading(true);
            setWholesalerError('');
            wholesalerDebounceRef.current = setTimeout(async () => {
                try {
                    const data = await getWholesalerById(trimmedId);
                    setWholesalerInfo(info => ({ ...info, name: data.merchantName || '' }));
                    setWholesalerError(data.merchantName ? '' : 'Wholesaler not found');
                } catch (err) {
                    setWholesalerInfo(info => ({ ...info, name: '' }));
                    setWholesalerError('Wholesaler not found');
                } finally {
                    setWholesalerLoading(false);
                }
            }, 500);
        }
    }, [wholesalerInfo.id, isKTEEmployee]);

    // Scroll to top when success modal is shown
    useEffect(() => {
        if (showSuccessModal) {
            scrollToTop();
        }
    }, [showSuccessModal]);

    // Render loading/error for role fetch
    if (roleLoading) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }
    if (roleError) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                    {roleError}
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{style}</style>
            <style>{`html { scroll-behavior: smooth; scroll-duration: 300ms; }`}</style>
            <div className="max-w-4xl mx-auto p-6">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Request</h1>
                    <p className="text-gray-600">{isKTEEmployee ? 'Create orders for Scratch and E-TopUp' : 'Create orders for E-PIN, Scratch, and E-TopUp'}</p>
                </div>

                {isKTEEmployee && (
                    <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start w-full sm:w-auto">
                        <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Wholesaler ID</label>
                            <input
                                ref={wholesalerIdInputRef}
                                type="text"
                                className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={wholesalerInfo.id}
                                onChange={e => setWholesalerInfo(info => ({ ...info, id: e.target.value }))}
                            />
                        </div>
                        <div className="flex flex-col min-h-[76px] w-full sm:w-auto"> {/* 40px input + 20px label + 16px error */}
                            <label className="block text-sm font-medium text-gray-700 mb-1">Wholesaler Name</label>
                            <input
                                type="text"
                                className={`w-full sm:w-60 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 cursor-not-allowed ${wholesalerError && !wholesalerInfo.name ? 'border-red-500' : ''}`}
                                value={wholesalerLoading ? 'Loading...' : wholesalerInfo.name}
                                readOnly
                                tabIndex={-1}
                                style={{ pointerEvents: 'none' }}
                            />
                            <div className="h-4"> {/* Reserve space for error */}
                                {!wholesalerInfo.name && wholesalerError && (
                                    <div className="text-xs text-red-500 mt-1 self-start">{wholesalerError}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="ml-2">
                    <div className="flex space-x-2">
                        {/* Conditionally render tabs based on userRole */}
                        {isKTEEmployee ? (
                            <>
                                <button
                                    type="button"
                                    className={`px-5 py-2 border rounded-t-md font-medium focus:outline-none transition-colors duration-100 ${activeTab === 'scratch' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-blue-100'}`}
                                    onClick={() => setActiveTab('scratch')}
                                >
                                    Scratch
                                </button>
                                <button
                                    type="button"
                                    className={`px-5 py-2 border rounded-t-md font-medium focus:outline-none transition-colors duration-100 ${activeTab === 'eTopUp' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-blue-100'}`}
                                    onClick={() => setActiveTab('eTopUp')}
                                >
                                    E-TopUp
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className={`px-5 py-2 border rounded-t-md font-medium focus:outline-none transition-colors duration-100 ${activeTab === 'ePin' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-blue-100'}`}
                                    onClick={() => setActiveTab('ePin')}
                                >
                                    E-PIN
                                </button>
                                <button
                                    type="button"
                                    className={`px-5 py-2 border rounded-t-md font-medium focus:outline-none transition-colors duration-100 ${activeTab === 'scratch' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-blue-100'}`}
                                    onClick={() => setActiveTab('scratch')}
                                >
                                    Scratch
                                </button>
                                <button
                                    type="button"
                                    className={`px-5 py-2 border rounded-t-md font-medium focus:outline-none transition-colors duration-100 ${activeTab === 'eTopUp' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-blue-100'}`}
                                    onClick={() => setActiveTab('eTopUp')}
                                >
                                    E-TopUp
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Conditionally render order sections based on activeTab and userRole */}
                    {activeTab === 'ePin' && !isKTEEmployee && renderOrderSection('ePin', 'E-PIN Orders')}
                    {activeTab === 'scratch' && renderScratchSection()}
                    {activeTab === 'eTopUp' && renderOrderSection('eTopUp', 'E-TopUp Orders')}

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                        {!isKTEEmployee && (
                            <div className="bg-white p-3 rounded-lg shadow-sm border">
                                <h3 className="text-base font-semibold text-gray-600">E-PIN Total</h3>
                                <p className="text-xl font-bold text-blue-600">IQD {formatNumber(ePinTotal)}</p>
                            </div>
                        )}
                        <div className="bg-white p-3 rounded-lg shadow-sm border">
                            <h3 className="text-base font-semibold text-gray-600">Scratch Card Total</h3>
                            <p className="text-xl font-bold text-green-600">IQD {formatNumber(scratchTotal)}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm border">
                            <h3 className="text-base font-semibold text-gray-600">E-TopUp Total</h3>
                            <p className="text-xl font-bold text-purple-600">IQD {formatNumber(eTopUpTotal)}</p>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mt-6">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-blue-900">Grand Total</h3>
                            <span className="text-2xl font-bold text-blue-900">
                                IQD {formatNumber(grandTotal)}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleReset}
                            disabled={isSubmitting}
                        >
                            Reset
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={grandTotal === 0 || isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Order'}
                        </Button>
                    </div>
                </form>
            </div>
            {/* Success Modal */}
            <Modal isOpen={showSuccessModal} onClose={() => { setShowSuccessModal(false); setSubmitStatus(null); }} hideCloseButton>
                <div className="flex flex-col items-center justify-center p-10">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                        <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <div className="mt-4 text-xl font-semibold text-black text-center">Order Submitted Successfully!</div>
                    {submitStatus?.orderId && (
                        <div className="mt-2 text-black text-center">Order ID: <span className="font-bold">{submitStatus.orderId}</span></div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default OrderRequest;
